import { setWorkerUrl } from 'maplibre-gl';
// vite/client's own types already cover the `?worker&url` suffix (resolves to a string URL).
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

/**
 * Shared MapLibre GL JS setup - one style URL, one marker-icon builder, used by every map
 * component. Replaces Leaflet's per-file `mkIcon`/tile-layer duplication (every map file used
 * to repeat the same divIcon HTML) so the visual style ("one look everywhere" across web and
 * mobile) actually stays in one place instead of drifting across ten copies.
 *
 * setWorkerUrl must run before any `new maplibregl.Map(...)` - MapLibre parses vector tiles in
 * a Web Worker, loaded at runtime as its own standalone request (`maplibre-gl-worker.mjs`), not
 * bundled into the main chunk. A plain `?url` import (or no explicit import at all) makes Vite's
 * production build emit nothing for that path: the request 404s, Caddy's SPA catch-all serves
 * index.html in its place, and the browser rejects it ("non-JavaScript MIME type text/html").
 * Every source tile then fetches fine but can never be parsed - symptom is a map that shows its
 * style's flat background colour and nothing else, no console error pointing at the real cause
 * unless you check Network for the failed .mjs request specifically. `?worker&url` (not `?url`)
 * routes the file through Vite's own worker bundling pipeline, which pulls in the worker's
 * sibling chunk (`maplibre-gl-shared.mjs`) it imports internally - `?url` alone emits the worker
 * file verbatim without that dependency, which loads but fails on its first import.
 */
setWorkerUrl(maplibreWorkerUrl);

// Self-hosted vector tiles (Britain & Ireland), served by tileserver-gl on the VPS behind
// Caddy at its own subdomain (see Vps/maps/). Not a secret - same reasoning the old OSM raster
// tile URL was hardcoded inline rather than configured per-environment.
export const MAP_STYLE_URL = 'https://test.tiles.transitdeal.com/styles/basic-preview/style.json';

export const MAP_ATTRIBUTION =
  '© <a href="https://www.openmaptiles.org/" target="_blank">OpenMapTiles</a> ' +
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap contributors</a>';

/**
 * Builds a round pin marker element - the MapLibre Marker equivalent of Leaflet's L.divIcon.
 * MapLibre's Marker API takes a real DOM element (not a canvas symbol layer), which keeps
 * markers queryable in tests the same way Leaflet's .leaflet-marker-icon elements were.
 */
export function createPinElement(label: string, color: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'td-map-pin';
  el.dataset.label = label;
  el.style.cssText =
    `background:${color};color:white;border-radius:50%;width:26px;height:26px;` +
    'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;' +
    'border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,.25);cursor:pointer;';
  el.textContent = label;
  return el;
}

/**
 * A small solid dot element - the MapLibre Marker equivalent of Leaflet's L.circleMarker
 * (used for dense point layers - traffic incidents, fleet vehicles - where a full pin label
 * would be too busy).
 */
export function createDotElement(color: string, sizePx = 18): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'td-map-dot';
  el.style.cssText =
    `background:${color};width:${sizePx}px;height:${sizePx}px;border-radius:50%;` +
    'border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:pointer;';
  return el;
}

/**
 * Geodesic circle as GeoJSON polygon coordinates, for the accurate-radius highlight Leaflet's
 * L.circle gave for free (radius in metres, not screen pixels - MapLibre's circle-radius paint
 * property is pixels only, which doesn't hold constant across zoom levels). No turf dependency
 * for one call site - this is the whole algorithm.
 */
/**
 * Decodes the standard encoded-polyline format (precision 5) the backend returns as
 * RouteResponse.encodedPolyline - the same algorithm as the C# PolylineCodec (Valhalla's own
 * precision-6 shape is decoded server-side and re-encoded at 5 before it ever reaches here, so
 * every client always decodes at the one precision).
 */
export function decodePolyline5(encoded: string): [number, number][] {
  const coords: [number, number][] = [];
  let index = 0, lat = 0, lng = 0;

  while (index < encoded.length) {
    let result = 0, shift = 0, b: number;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    result = 0; shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    coords.push([lng / 1e5, lat / 1e5]); // [lng, lat] - MapLibre/GeoJSON order
  }
  return coords;
}

export function circlePolygonCoords(
  lat: number, lng: number, radiusMetres: number, points = 64
): [number, number][] {
  const coords: [number, number][] = [];
  const earthRadius = 6378137; // metres
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dLat = (radiusMetres * Math.cos(angle)) / earthRadius;
    const dLng = (radiusMetres * Math.sin(angle)) / (earthRadius * Math.cos(lat * Math.PI / 180));
    coords.push([lng + dLng * 180 / Math.PI, lat + dLat * 180 / Math.PI]);
  }
  return coords;
}
